import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

const DOCS = [
  { key: "google-business", emoji: "📍", label: "Fiche Google — étapes de connexion", desc: "Demande d'accès API Business Profile (à lancer avant le lot 3)" },
  { key: "plan-scenario3", emoji: "🧭", label: "Plan scénario 3 — reconstruction marketing (31 oct. 2026)", desc: "Lots, 12 contrôles, coûts, migration — à approuver" },
  { key: "audit-generateur", emoji: "🔎", label: "Audit du générateur de contenu (31 oct. 2026)", desc: "Comparatif Holo / GrandRanker / FameHero, causes racines, 3 scénarios, plan" },
  { key: "todo-master", emoji: "📋", label: "TODO MASTER LIST", desc: "Tout ce qui reste à faire — priorités P0/P1/P2" },
  { key: "tundra-wrap", emoji: "🛻", label: "Lettrage Camion Tundra 2026", desc: "3 concepts wrap + mockups visuels + reco Kaizen" },
  { key: "master-index", emoji: "🥋", label: "Master Index Plateforme", desc: "Source officielle de vérité unique" },
  { key: "liens-validation", emoji: "🔗", label: "Liens de validation complets", desc: "Toutes les URLs à tester" },
  { key: "manifeste", emoji: "📜", label: "Manifeste Figer/Classer/Construire", desc: "Décision stratégique 29 mai 2026" },
  { key: "spec-billetterie", emoji: "🎟️", label: "Spec Billetterie premium", desc: "Blueprint Phase 1" },
  { key: "spec-livestream", emoji: "📺", label: "Spec Livestream payant", desc: "Blueprint Phase 2 (10 blocs)" },
  { key: "spec-ai-operator", emoji: "🤖", label: "Spec AI Operator", desc: "Chief of Staff 24/7" },
  { key: "spec-avatar-onboarding", emoji: "👤", label: "Spec Avatar Onboarding", desc: "Guide de plateforme" },
];

const TUNDRA_CONCEPTS = [
  { key: "tundra-concept-1", title: "Concept 1 — Minimal Premium", subtitle: "Style horloger · Maximum contraste noir/blanc", color: "border-zinc-700" },
  { key: "tundra-concept-2", title: "Concept 2 — Franchise-Ready Executive", subtitle: "🏆 Recommandation Kaizen · Le véhicule franchise national", color: "border-amber-500/50" },
  { key: "tundra-concept-3", title: "Concept 3 — Event & Media Focused", subtitle: "Modules détachables événementiels", color: "border-red-500/40" },
];

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function DocsViewerModule() {
  const location = useLocation();
  const initialKey = location.pathname.includes("lettrage-camion") ? "tundra-wrap" : "todo-master";
  const [selected, setSelected] = useState(initialKey);
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async (key) => {
    setBusy(true);
    setDoc(null);
    try {
      const r = await api.get(`/memory-docs/${key}`);
      setDoc(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(selected); }, [selected]);

  const printDoc = () => {
    window.print();
  };

  const downloadMarkdown = () => {
    if (!doc) return;
    const blob = new Blob([doc.content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.path.split("/").pop();
    a.click();
    URL.revokeObjectURL(url);
    toast.success("📥 Markdown téléchargé");
  };

  const currentDoc = DOCS.find((d) => d.key === selected);

  return (
    <div className="space-y-6" data-testid="docs-viewer-module">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .print-area, .print-area * { visibility: visible !important; }
          .print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; color: black !important; }
          .print-area * { color: black !important; background: white !important; border-color: #ccc !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Header - hidden when printing */}
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black no-print">
        <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">📚 DOCUMENTS KAIZEN</div>
        <h1 className="text-3xl font-bold tracking-tight">Bibliothèque des specs et blueprints</h1>
        <p className="text-zinc-500 text-sm mt-2">
          Tous les documents stratégiques de la plateforme : design lettrage Tundra, master index, manifestes Kaizen, specs modules.
        </p>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        {/* Sidebar - hidden when printing */}
        <div className="space-y-1 no-print" data-testid="docs-list">
          <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-2">Documents</div>
          {DOCS.map((d) => (
            <button
              key={d.key}
              onClick={() => setSelected(d.key)}
              className={`w-full text-left border rounded-lg p-3 text-xs transition ${selected === d.key ? "border-amber-500/40 bg-amber-500/10" : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-600"}`}
              data-testid={`doc-btn-${d.key}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-base">{d.emoji}</span>
                <span className="font-semibold text-zinc-100 truncate">{d.label}</span>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 leading-relaxed">{d.desc}</div>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="lg:col-span-3 print-area" data-testid="doc-content-area">
          <div className="border border-zinc-800 rounded-2xl bg-zinc-950/40">
            {/* Top bar - hidden when printing */}
            <div className="flex items-center justify-between flex-wrap gap-2 px-5 py-3 border-b border-zinc-800 no-print">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="text-2xl">{currentDoc?.emoji}</span>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-zinc-100 truncate">{currentDoc?.label}</div>
                  {doc && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge variant="outline" className="text-[10px]">{doc.size_kb} KB</Badge>
                      <span className="text-[10px] text-zinc-500 font-mono truncate">{doc.path}</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" onClick={downloadMarkdown} disabled={!doc} variant="outline" data-testid="download-md-btn" className="text-xs">📥 .md</Button>
                <Button size="sm" onClick={printDoc} disabled={!doc} className="bg-amber-500 hover:bg-amber-400 text-black text-xs" data-testid="print-btn">🖨️ Imprimer / PDF</Button>
              </div>
            </div>

            {/* Markdown content */}
            <div className="p-5 max-h-[80vh] overflow-y-auto" data-testid="doc-content">
              {busy && <div className="text-zinc-500 text-sm">Chargement…</div>}

              {/* Tundra visual gallery — only when tundra-wrap doc selected */}
              {!busy && selected === "tundra-wrap" && (
                <div className="mb-8" data-testid="tundra-gallery">
                  <div className="text-[10px] tracking-[0.4em] text-amber-400 mb-3">🎨 MOCKUPS VISUELS DES 3 CONCEPTS</div>
                  <div className="grid md:grid-cols-3 gap-3 mb-6">
                    {TUNDRA_CONCEPTS.map((c) => (
                      <TundraConceptImage key={c.key} concept={c} />
                    ))}
                  </div>
                  <div className="text-[10px] text-zinc-500 italic mb-4">
                    💡 Mockups générés par IA (Gemini Nano Banana) — usage : présentation interne, mockup designer pro requis avant pose réelle.
                  </div>
                </div>
              )}

              {!busy && doc && (
                <article className="prose prose-invert prose-sm max-w-none prose-headings:tracking-tight prose-headings:text-zinc-100 prose-a:text-amber-400 prose-strong:text-amber-300 prose-code:text-amber-200 prose-code:bg-black/50 prose-code:px-1 prose-code:rounded prose-table:text-xs prose-th:text-amber-400 prose-th:font-bold prose-th:border-zinc-700 prose-td:border-zinc-800 prose-blockquote:border-amber-500/40 prose-blockquote:text-zinc-300">
                  <ReactMarkdown>{doc.content}</ReactMarkdown>
                </article>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TundraConceptImage({ concept }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let revoke = null;
    api.get(`/memory-docs/images/${concept.key}`, { responseType: "blob" })
      .then((r) => {
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setSrc(url);
      })
      .catch(() => setError(true));
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [concept.key]);

  const downloadImage = () => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = `${concept.key}.png`;
    a.click();
    toast.success("📥 Image téléchargée");
  };

  return (
    <div className={`border rounded-2xl overflow-hidden ${concept.color} bg-black/30`} data-testid={`concept-card-${concept.key}`}>
      <div className="aspect-video bg-zinc-900 relative">
        {error ? (
          <div className="flex items-center justify-center h-full text-zinc-500 text-xs">❌ Image indisponible</div>
        ) : !src ? (
          <div className="flex items-center justify-center h-full text-zinc-500 text-xs animate-pulse">🎨 Chargement…</div>
        ) : (
          <img
            src={src}
            alt={concept.title}
            className="w-full h-full object-cover cursor-zoom-in"
            onClick={() => window.open(src, "_blank")}
            data-testid={`concept-img-${concept.key}`}
          />
        )}
      </div>
      <div className="p-3">
        <div className="text-sm font-bold text-zinc-100">{concept.title}</div>
        <div className="text-[10px] text-zinc-400 mt-0.5">{concept.subtitle}</div>
        {src && (
          <button onClick={downloadImage} className="mt-2 text-[10px] text-amber-300 hover:text-amber-200 border border-amber-500/40 rounded-full px-2 py-0.5" data-testid={`download-img-${concept.key}`}>
            📥 Télécharger PNG
          </button>
        )}
      </div>
    </div>
  );
}
